import { useUnsavedChanges } from "../../layout/AppShellContext";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { useActiveCompanyId } from "../../api/useActiveCompanyId";
import { MobileMenu } from "../Dashboard/MobileMenu";
import { MenuHeader } from "../Dashboard/block/MenuHeader/MenuHeader";
import {
    buildMarkingScannerMessage,
    buildMarkingScanPayload,
    buildNativeMarkingDeepLink,
    createMarkingRequestId,
    MARKING_SCAN_CANCEL_EVENT,
    MARKING_SCAN_RESULT_EVENT,
    markingErrorMessage,
    MarkingNativeWindow,
    NATIVE_SESSION_BOOTSTRAP_EVENT,
    supportsNativeDataMatrixScanner,
} from "./markingBridge";
import "./TerminalMarkingPage.scss";
import { MarkingDemo } from "./MarkingDemo";

type MarkingSession = {
    token: string;
    status: string;
    lineId?: string | null;
    itemName?: string | null;
    unitIndex?: number | null;
    goodTitle?: string | null;
    gtin?: string | null;
    expectedGtin?: string | null;
    gtinTail?: string | null;
    expiresAt?: string | null;
    terminal?: {
        id?: number | string | null;
        key?: string | null;
        title?: string | null;
    } | null;
};

type CompanyListResponse = {
    items?: Array<{
        id?: number | string | null;
        address?: string | null;
        title?: string | null;
        legalName?: string | null;
    }>;
};

type PendingSessionsResponse = {
    items?: MarkingSession[];
    serverTime?: string;
};

type NativeScanDetail = {
    requestId?: string | null;
    sessionToken?: string | null;
    format?: string | null;
    dataMatrix?: string | null;
};

type PendingScan = {
    requestId: string;
    token: string;
};

function formatExpiresAt(value?: string | null): string {
    if (!value) return "";
    const date = new Date(value);

    return Number.isNaN(date.getTime())
        ? ""
        : date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function formatGtinTail(session: MarkingSession): string {
    const suppliedTail = typeof session.gtinTail === "string" ? session.gtinTail : "";
    const fullGtin = typeof session.expectedGtin === "string"
        ? session.expectedGtin
        : typeof session.gtin === "string"
            ? session.gtin
            : "";
    const normalized = (suppliedTail || fullGtin).replace(/\s+/g, "");

    return normalized ? `GTIN …${normalized.slice(-6)}` : "";
}

export const TerminalMarkingPage: React.FC = () => {
    const [demo, setDemo] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [sessions, setSessions] = useState<MarkingSession[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [submittingToken, setSubmittingToken] = useState("");
    const [pendingScan, setPendingScan] = useState<PendingScan | null>(null);
    const [nativeScannerAvailable, setNativeScannerAvailable] = useState(() => supportsNativeDataMatrixScanner());
    const [selectedCompanyLabel, setSelectedCompanyLabel] = useState("");
    const pendingScanRef = useRef<PendingScan | null>(null);
    const { value: companyId, resolved: companyResolved } = useActiveCompanyId();
    const companyRef = useRef(companyId); companyRef.current = companyId;
    const sessionRequest = useRef(0);
    useUnsavedChanges(!!pendingScan || !!submittingToken, "Сканирование маркировки");
    useEffect(() => {
        const counter = sessionRequest; ++counter.current;
        pendingScanRef.current = null; setPendingScan(null); setSubmittingToken(""); setSessions([]); setError(""); setNotice(""); setDemo(false);
        return () => { ++counter.current; pendingScanRef.current = null; };
    }, [companyId]);

    const refreshCapability = useCallback(() => {
        setNativeScannerAvailable(supportsNativeDataMatrixScanner());
    }, []);

    useEffect(() => {
        window.addEventListener(NATIVE_SESSION_BOOTSTRAP_EVENT, refreshCapability);
        window.addEventListener("pageshow", refreshCapability);
        refreshCapability();

        return () => {
            window.removeEventListener(NATIVE_SESSION_BOOTSTRAP_EVENT, refreshCapability);
            window.removeEventListener("pageshow", refreshCapability);
        };
    }, [refreshCapability]);

    useEffect(() => {
        if (!companyResolved || !companyId) {
            setSelectedCompanyLabel("");
            return;
        }

        let cancelled = false;
        api.get<CompanyListResponse>("/api/me/companies")
            .then((response) => {
                if (cancelled) return;
                const selected = (response?.items || []).find((item) => String(item.id ?? "") === companyId);
                setSelectedCompanyLabel(
                    String(selected?.address || selected?.title || selected?.legalName || `Салон #${companyId}`),
                );
            })
            .catch(() => {
                if (!cancelled) setSelectedCompanyLabel(`Салон #${companyId}`);
            });

        return () => {
            cancelled = true;
        };
    }, [companyId, companyResolved]);

    const loadSessions = useCallback(async (silent = false) => {
        const epoch = ++sessionRequest.current;
        if (demo) { setLoading(false); return; }
        if (!companyResolved) return;
        if (!companyId) {
            setSessions([]);
            setLoading(false);
            setError("Выберите салон, чтобы увидеть запросы терминала.");
            return;
        }

        if (!silent) setLoading(true);
        try {
            const response = await api.get<PendingSessionsResponse>("/api/gateway/marking/sessions/pending", {}, { headers: { "X-Company-Id": companyId } });
            if (epoch !== sessionRequest.current || companyId !== companyRef.current) return;
            setSessions(Array.isArray(response?.items) ? response.items : []);
            setError("");
        } catch (requestError) {
            if (epoch === sessionRequest.current && companyId === companyRef.current && !silent) setError(markingErrorMessage(requestError, "load"));
        } finally {
            if (epoch === sessionRequest.current && companyId === companyRef.current) setLoading(false);
        }
    }, [companyId, companyResolved, demo]);

    useEffect(() => {
        void loadSessions();
        if (demo || !companyResolved || !companyId) return;

        const poll = () => {
            if (document.visibilityState === "visible" && !pendingScanRef.current && !submittingToken) {
                void loadSessions(true);
            }
        };
        const timer = window.setInterval(poll, 5_000);
        document.addEventListener("visibilitychange", poll);

        return () => {
            window.clearInterval(timer);
            document.removeEventListener("visibilitychange", poll);
        };
    }, [companyId, companyResolved, loadSessions, submittingToken, demo]);

    const submitScan = useCallback(async (pending: PendingScan, dataMatrix: string) => {
        if (!companyId || pendingScanRef.current?.requestId !== pending.requestId) return;
        const stillCurrent = () => companyId === companyRef.current && pendingScanRef.current?.requestId === pending.requestId;
        setSubmittingToken(pending.token);
        setError("");
        setNotice("");
        try {
            await api.post(
                `/api/gateway/marking/sessions/${encodeURIComponent(pending.token)}/scan`,
                buildMarkingScanPayload(dataMatrix, pending.requestId),
                { headers: { "X-Company-Id": companyId } },
            );
            if (!stillCurrent()) return;
            setNotice("DataMatrix принят. На терминале можно продолжить продажу.");
            await loadSessions(true);
        } catch (requestError) {
            if (stillCurrent()) setError(markingErrorMessage(requestError, "submit"));
        } finally {
            if (stillCurrent()) { setSubmittingToken(""); pendingScanRef.current = null; setPendingScan(null); }
        }
    }, [loadSessions, companyId]);

    useEffect(() => {
        const handleResult = (event: Event) => {
            const detail = ((event as CustomEvent).detail || {}) as NativeScanDetail;
            const active = pendingScanRef.current;
            if (!active || detail.requestId !== active.requestId) return;
            if (detail.sessionToken && detail.sessionToken !== active.token) return;
            if (detail.format && detail.format !== "datamatrix") {
                pendingScanRef.current = null;
                setPendingScan(null);
                setError("Сканер вернул не DataMatrix. Используйте камеру маркировки в приложении.");
                return;
            }

            // A string check avoids coercion and preserves every GS/FNC1 byte.
            const dataMatrix = typeof detail.dataMatrix === "string" ? detail.dataMatrix : "";
            if (dataMatrix === "") {
                pendingScanRef.current = null;
                setPendingScan(null);
                setError("Камера не распознала DataMatrix. Попробуйте ещё раз.");
                return;
            }
            void submitScan(active, dataMatrix);
        };

        const handleCancel = (event: Event) => {
            const detail = ((event as CustomEvent).detail || {}) as NativeScanDetail;
            const active = pendingScanRef.current;
            if (!active || detail.requestId !== active.requestId) return;

            pendingScanRef.current = null;
            setPendingScan(null);
            setNotice("Сканирование отменено. Запрос остаётся в списке.");
        };

        window.addEventListener(MARKING_SCAN_RESULT_EVENT, handleResult);
        window.addEventListener(MARKING_SCAN_CANCEL_EVENT, handleCancel);
        return () => {
            window.removeEventListener(MARKING_SCAN_RESULT_EVENT, handleResult);
            window.removeEventListener(MARKING_SCAN_CANCEL_EVENT, handleCancel);
        };
    }, [submitScan]);

    const openScanner = (session: MarkingSession) => {
        const nativeWindow = window as MarkingNativeWindow;
        const nextPending = {
            requestId: createMarkingRequestId(),
            token: session.token,
        };
        if (!supportsNativeDataMatrixScanner(nativeWindow)) {
            setNativeScannerAvailable(false);
            if (!companyId) {
                setError("Выберите салон перед сканированием DataMatrix.");
                return;
            }
            setError("");
            setNotice("Открываем нативный DataMatrix-сканер в приложении Chio Chio…");
            window.location.assign(buildNativeMarkingDeepLink(
                nextPending.requestId,
                session.token,
                companyId,
                window.location.href,
            ));
            return;
        }

        pendingScanRef.current = nextPending;
        setPendingScan(nextPending);
        setError("");
        setNotice("Открываем камеру DataMatrix…");
        try {
            nativeWindow.ReactNativeWebView!.postMessage(JSON.stringify(
                buildMarkingScannerMessage(nextPending.requestId, session.token, "lkfr_marking"),
            ));
        } catch {
            pendingScanRef.current = null;
            setPendingScan(null);
            setError("Не удалось открыть DataMatrix-сканер. Перезапустите мобильное приложение и повторите попытку.");
        }
    };

    return (
        <div className="terminal-marking-page">
            <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
            <MenuHeader onMenuClick={() => setMenuOpen(true)} />

            <main className="terminal-marking-content">
                <div className="terminal-marking-header">
                    <div>
                        <h1>Маркировка товаров</h1>
                        <p>Запросы DataMatrix от терминалов выбранного салона</p>
                        <div className="terminal-marking-company">
                            Салон: {selectedCompanyLabel || (companyId ? `#${companyId}` : "не выбран")}
                        </div>
                    </div>
                    <button type="button" onClick={() => void loadSessions()} disabled={loading || demo || !!pendingScan || !!submittingToken}>
                        {loading ? "Обновление…" : "Обновить"}
                    </button>
                </div>

                {demo ? <MarkingDemo onClose={() => setDemo(false)} /> : <section className="terminal-marking-guide"><h2>Зачем нужна маркировка?</h2><p>Помогает передать терминалу DataMatrix конкретной упаковки при продаже маркированного товара. Телефон сканирует код, терминал получает его и продолжает оформление продажи.</p><button type="button" disabled={!!pendingScan || !!submittingToken} onClick={() => setDemo(true)}>Попробовать демо</button></section>}

                {!demo && !nativeScannerAvailable ? (
                    <div className="terminal-marking-message warning" role="status">
                        По кнопке сканирования откроется актуальная версия мобильного приложения Chio Chio.
                        Камера браузера и обычный QR-сканер не используются.
                    </div>
                ) : null}
                {!demo && error ? <div className="terminal-marking-message error" role="alert">{error}</div> : null}
                {!demo && notice ? <div className="terminal-marking-message success" role="status">{notice}</div> : null}

                {demo ? null : loading ? (
                    <div className="terminal-marking-empty">Загружаем запросы…</div>
                ) : sessions.length === 0 ? (
                    <div className="terminal-marking-empty">Активных запросов маркировки нет.</div>
                ) : (
                    <div className="terminal-marking-list">
                        {sessions.map((session) => {
                            const terminalName = session.terminal?.title || session.terminal?.key || "Терминал";
                            const expiresAt = formatExpiresAt(session.expiresAt);
                            const gtinTail = formatGtinTail(session);
                            const isSubmitting = submittingToken === session.token;
                            const isScanning = pendingScan?.token === session.token;

                            return (
                                <article className="terminal-marking-card" key={session.token}>
                                    <div className="terminal-marking-card-copy">
                                        <strong>{session.itemName || session.goodTitle || "Маркированный товар"}</strong>
                                        <span>
                                            {terminalName}
                                            {session.unitIndex != null ? ` · единица ${session.unitIndex}` : ""}
                                            {gtinTail ? ` · ${gtinTail}` : ""}
                                            {expiresAt ? ` · до ${expiresAt}` : ""}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        disabled={isSubmitting || pendingScan !== null}
                                        onClick={() => openScanner(session)}
                                    >
                                        {isSubmitting ? "Отправляем…" : isScanning ? "Сканируем…" : nativeScannerAvailable ? "Сканировать DataMatrix" : "Открыть приложение"}
                                    </button>
                                </article>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
};

export default TerminalMarkingPage;
